import { useGesture } from "@use-gesture/react";
import { useEffect, useRef } from "react";

import type { BoundingBox, Point } from "../types.ts";
import { MAX_ZOOM, MIN_ZOOM, WHEEL_ZOOM_STEP } from "./constants.ts";
import { useConversion } from "./ConversionContext";
import { useDimensions } from "./DimensionsContext";
import { usePan } from "./PanContext";
import { useZoom } from "./ZoomContext.ts";

const getCoordsFromCenter = (rect: DOMRect | undefined, coords: Point): Point => {
  if (!rect) return coords;

  const rectCenterCoords = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
  return {
    x: coords.x - rectCenterCoords.x,
    y: coords.y - rectCenterCoords.y,
  };
};

const mouseDisplacement = 50;

const mouseVelocity = (velocity: number) => Math.max(velocity * 1.5, 1);
const trackpadVelocity = (velocity: number) => Math.max(velocity * 0.5, 1);

// A trackpad streams many small events per gesture where a mouse emits a few large ticks.
const TRACKPAD_INVOCATIONS = 12;

const clampZoom = (zoom: number) => Math.min(Math.max(MIN_ZOOM, zoom), MAX_ZOOM);

// The viewport is measured once per gesture: it can't move mid-flick, and measuring per event
// forces a layout on every frame of a zoom.
type WheelMemo = {
  zooming: boolean;
  invocations: number;
  zoom: number;
  translation: Point;
  viewportRect: DOMRect | undefined;
};

type PinchMemo = {
  distance: number;
  origin: Point;
  viewportRect: DOMRect | undefined;
};

export const GestureHandler = ({
  children,
  onMapBoundsChange,
}: {
  children: React.ReactNode;
  onMapBoundsChange: (mapBounds: BoundingBox) => void;
}) => {
  const { onZoomGesture, onZoomWheel, zoom } = useZoom();
  const { onPanGesture } = usePan();
  const { viewportSize, viewportRef, setViewportRef } = useDimensions();
  const { mapBounds } = useConversion();

  const mapBoundsRef = useRef<BoundingBox | null>(null);

  useGesture(
    {
      onWheel: ({ event, delta: [dx, dy], movement: [, my], velocity: [vx, vy], memo }) => {
        const wheelMemo: WheelMemo = memo ?? {
          zooming: event.ctrlKey || event.metaKey,
          invocations: 0,
          zoom,
          translation: { x: 0, y: 0 },
          viewportRect: viewportRef.current?.getBoundingClientRect(),
        };

        // The modifier held when the gesture started decides what it does - releasing it part way
        // through a trackpad flick shouldn't turn a zoom into a pan.
        if (wheelMemo.zooming !== (event.ctrlKey || event.metaKey)) return wheelMemo;

        event.preventDefault();

        const invocations = wheelMemo.invocations + 1;
        const usingTrackpad = invocations > TRACKPAD_INVOCATIONS;
        const origin = getCoordsFromCenter(wheelMemo.viewportRect, { x: event.clientX, y: event.clientY });

        if (wheelMemo.zooming) {
          // Mouse wheel: a fixed step per tick. Velocity-amplifying it makes a fast flick blow past the
          // clamp range in one gesture, since a wheel emits more ticks rather than bigger ones. A trackpad
          // streams many small events, so dampening by velocity helps there.
          const rawZoom = usingTrackpad
            ? wheelMemo.zoom - dy / (100 * trackpadVelocity(vy))
            : wheelMemo.zoom - Math.sign(my) * WHEEL_ZOOM_STEP;

          const newZoom = Number(clampZoom(rawZoom).toFixed(2));

          onZoomWheel(newZoom, origin);

          return { ...wheelMemo, invocations, zoom: newZoom };
        }

        const [panX, panY] = usingTrackpad
          ? [-dx / trackpadVelocity(vx), -dy / trackpadVelocity(vy)]
          : [0, -Math.sign(dy) * (mouseDisplacement * mouseVelocity(vy))];

        // Shift scrolls sideways, and on macOS a mouse still reports that movement on the y axis.
        const translation = event.shiftKey
          ? { x: wheelMemo.translation.x + (panX || panY), y: wheelMemo.translation.y }
          : { x: wheelMemo.translation.x + panX, y: wheelMemo.translation.y + panY };

        onPanGesture({ scale: 1, translation, origin });

        return { ...wheelMemo, invocations, translation };
      },

      onWheelEnd: () => onPanGesture(undefined),

      // A trackpad pinch arrives as a ctrl + wheel event and is handled above, so only touch pinches
      // are taken here - two fingers zoom about their midpoint and pan it at the same time.
      onPinch: ({ event, da: [distance], origin: [originX, originY], first, last, memo }) => {
        if (event instanceof WheelEvent) return;

        if (last) {
          onZoomGesture(undefined);
          onPanGesture(undefined);
          return;
        }

        const pinchMemo: PinchMemo | undefined = memo;

        if (first || !pinchMemo) {
          return {
            distance,
            origin: { x: originX, y: originY },
            viewportRect: viewportRef.current?.getBoundingClientRect(),
          } satisfies PinchMemo;
        }

        const origin = getCoordsFromCenter(pinchMemo.viewportRect, pinchMemo.origin);

        onZoomGesture({ scale: distance / pinchMemo.distance, translation: { x: 0, y: 0 }, origin });
        onPanGesture({
          scale: distance / pinchMemo.distance,
          translation: { x: originX - pinchMemo.origin.x, y: originY - pinchMemo.origin.y },
          origin,
        });

        return pinchMemo;
      },
    },
    { target: viewportRef, eventOptions: { capture: true, passive: false } },
  );

  useEffect(() => {
    if (!viewportSize || mapBoundsRef.current === mapBounds) return;

    mapBoundsRef.current = mapBounds;
    onMapBoundsChange(mapBounds);
  }, [viewportSize, onMapBoundsChange, mapBounds]);

  return (
    <div ref={setViewportRef} style={{ height: "100%" }} data-test="gesture-handler">
      {children}
    </div>
  );
};
